// src/features/booking/BookingTypeDialog.tsx
// Register / update a booking type definition (admin). Desktop-app create
// dialog: display name is the ghost title, slug is a mono field (locked once
// created), toggles for the four behavior flags hide behind "+ more fields".

import { useEffect, useRef, useState } from 'react';
import { useI18n } from '@react/hooks';
import { Alert } from '@react/components';
import { FormDialog, TitleInput, AutoGrowTextarea, FormRow, SegmentedControl } from '@/components/form';
import { Select } from '@/components/Select';
import type { SelectOption } from '@/components/Select';
import { ToggleSwitch } from '@/components/ToggleSwitch';
import { durationToMinutes, errMessage, minutesToDuration } from './lib';
import { RegisterBookingType } from '@core/api/endpoints/booking/register_booking_type';
import { UpdateBookingType } from '@core/api/endpoints/booking/update_booking_type';
import type { BookingTypeDefinition, CapacityType, PricingModel } from '@core/types/booking';

interface Props {
  open: boolean;
  onClose: () => void;
  type: BookingTypeDefinition | null;
  scope: { productId: string; platformId: string; tenantId: string };
  onSaved: () => void;
}

const PRICING_MODELS: PricingModel[] = ['PRICING_MODEL_FIXED', 'PRICING_MODEL_HOURLY', 'PRICING_MODEL_DAILY', 'PRICING_MODEL_PER_SESSION', 'PRICING_MODEL_PER_PERSON', 'PRICING_MODEL_CUSTOM'];
const CAPACITY_TYPES: CapacityType[] = ['CAPACITY_TYPE_SINGLE', 'CAPACITY_TYPE_MULTI', 'CAPACITY_TYPE_UNLIMITED'];

function digits(v: string): string {
  return v.replace(/[^0-9]/g, '');
}

function slugify(v: string): string {
  return v.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
}

export function BookingTypeDialog({ open, onClose, type, scope, onSaved }: Props) {
  const { t } = useI18n();
  const isEdit = !!type;
  const descRef = useRef<HTMLTextAreaElement>(null);

  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [pricingModel, setPricingModel] = useState<PricingModel>('PRICING_MODEL_HOURLY');
  const [capacityType, setCapacityType] = useState<CapacityType>('CAPACITY_TYPE_SINGLE');
  const [defaultDurationMin, setDefaultDurationMin] = useState('60');
  const [requiresApproval, setRequiresApproval] = useState(false);
  const [allowInstant, setAllowInstant] = useState(true);
  const [enableWaitlist, setEnableWaitlist] = useState(true);
  const [enableReviews, setEnableReviews] = useState(true);
  const [moreFields, setMoreFields] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (type) {
      setSlug(type.slug ?? '');
      setSlugTouched(true);
      setDisplayName(type.display_name ?? '');
      setDescription(type.description ?? '');
      setPricingModel(type.pricing_model ?? 'PRICING_MODEL_HOURLY');
      setCapacityType(type.capacity_type ?? 'CAPACITY_TYPE_SINGLE');
      setDefaultDurationMin(String(durationToMinutes(type.default_duration) || 60));
      setRequiresApproval(!!type.requires_provider_approval);
      setAllowInstant(!!type.allow_instant_booking);
      setEnableWaitlist(!!type.enable_waitlist);
      setEnableReviews(!!type.enable_reviews);
    } else {
      setSlug('');
      setSlugTouched(false);
      setDisplayName('');
      setDescription('');
      setPricingModel('PRICING_MODEL_HOURLY');
      setCapacityType('CAPACITY_TYPE_SINGLE');
      setDefaultDurationMin('60');
      setRequiresApproval(false);
      setAllowInstant(true);
      setEnableWaitlist(true);
      setEnableReviews(true);
    }
    setMoreFields(false);
    setError(null);
  }, [open, type]);

  const pricingOptions: SelectOption[] = PRICING_MODELS.map((m) => ({ value: m, label: t(`booking.resource.pricingModel.${m.replace('PRICING_MODEL_', '').toLowerCase()}`) }));
  const capacityOptions = CAPACITY_TYPES.map((c) => ({ value: c, label: t(`booking.admin.types.capacityType.${c.replace('CAPACITY_TYPE_', '').toLowerCase()}`) }));

  const dirty = !!(displayName.trim() || description.trim() || slug.trim());

  const submit = async () => {
    if (!slug.trim() || !displayName.trim()) {
      setError(t('booking.admin.types.requiredFields'));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (isEdit && type?.entity) {
        await UpdateBookingType(type.entity.id, {
          definition: {
            ...type,
            slug,
            display_name: displayName,
            description,
            pricing_model: pricingModel,
            capacity_type: capacityType,
            default_duration: minutesToDuration(parseInt(defaultDurationMin || '60', 10)),
            requires_provider_approval: requiresApproval,
            allow_instant_booking: allowInstant,
            enable_waitlist: enableWaitlist,
            enable_reviews: enableReviews,
          },
          update_mask: ['slug', 'display_name', 'description', 'pricing_model', 'capacity_type', 'default_duration', 'requires_provider_approval', 'allow_instant_booking', 'enable_waitlist', 'enable_reviews'],
        });
      } else {
        await RegisterBookingType({
          entity: { type_id: '', type_name: 'booking_type_definition', id: '', platform_id: scope.platformId, tenant_id: scope.tenantId, product_id: scope.productId },
          definition: {
            slug,
            display_name: displayName,
            description,
            product_id: scope.productId,
            source_entity_type: 'booking',
            pricing_model: pricingModel,
            default_currency: 'USD',
            capacity_type: capacityType,
            default_capacity: 1,
            requires_provider_approval: requiresApproval,
            allow_instant_booking: allowInstant,
            enable_waitlist: enableWaitlist,
            default_duration: minutesToDuration(parseInt(defaultDurationMin || '60', 10)),
            calendar_event_type_slug: '',
            blocks_provider_availability: true,
            blocks_customer_availability: false,
            enable_reviews: enableReviews,
            enable_provider_reviews: false,
            enable_deposit: false,
            enable_payment: false,
            enable_rescheduling: true,
            is_active: true,
            source_created_topic: '',
            source_updated_topic: '',
            source_deleted_topic: '',
            source_status_changed_topic: '',
          },
        });
      }
      setSaving(false);
      onSaved();
      onClose();
    } catch (e) {
      setSaving(false);
      setError(errMessage(e, t('booking.admin.types.saveError')));
    }
  };

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      onSubmit={submit}
      submitLabel={isEdit ? t('common.saveChanges') : t('booking.admin.types.createSubmit')}
      canSubmit={!!slug.trim() && !!displayName.trim()}
      isSubmitting={saving}
      dirty={dirty}
      aria-label={isEdit ? t('booking.admin.types.editTitle') : t('booking.admin.types.createTitle')}
      footerLeft={
        <button
          type="button"
          onClick={() => setMoreFields((v) => !v)}
          className="text-xs text-muted-foreground hover:text-foreground"
        >
          {moreFields ? t('formKit.fewerFields') : t('formKit.moreFields')}
        </button>
      }
    >
      <div className="px-3.5 pb-1 pt-2">
        {error && (
          <div className="pb-2">
            <Alert variant="error">{error}</Alert>
          </div>
        )}
        <TitleInput
          value={displayName}
          onChange={(v) => {
            setDisplayName(v);
            if (!slugTouched && !isEdit) setSlug(slugify(v));
          }}
          placeholder={`${t('booking.admin.types.field.displayName')}…`}
          initialFocus
          onEnter={() => descRef.current?.focus()}
          aria-label={t('booking.admin.types.field.displayName')}
        />
        <AutoGrowTextarea
          ref={descRef}
          value={description}
          onChange={setDescription}
          placeholder={`${t('booking.admin.types.field.description')}…`}
          minRows={1}
        />
      </div>

      <div className="px-3.5 pb-2">
        <FormRow label={t('booking.admin.types.field.slug')} labelWidth="sm">
          <input
            value={slug}
            onChange={(e) => {
              setSlugTouched(true);
              setSlug(slugify(e.target.value));
            }}
            disabled={isEdit}
            aria-label={t('booking.admin.types.field.slug')}
            className="w-full rounded-md border border-input bg-background px-2.5 py-1.5 font-mono text-sm text-foreground outline-none focus:ring-2 focus:ring-ring disabled:opacity-60"
          />
        </FormRow>
      </div>

      <div className="flex flex-wrap items-center gap-2 px-3.5 pb-3">
        <div className="w-40">
          <Select value={pricingModel} onChange={(e) => setPricingModel(e.target.value as PricingModel)} options={pricingOptions} aria-label={t('booking.resource.field.pricingModel')} fullWidth />
        </div>
        <SegmentedControl
          size="sm"
          value={capacityType}
          onChange={(v) => setCapacityType(v as CapacityType)}
          options={capacityOptions}
          aria-label={t('booking.admin.types.field.capacityType')}
        />
        <span className="inline-flex items-center gap-1.5 rounded-lg bg-muted px-2.5 py-1">
          <input
            type="text"
            inputMode="numeric"
            value={defaultDurationMin}
            onChange={(e) => setDefaultDurationMin(digits(e.target.value))}
            aria-label={t('booking.admin.types.field.defaultDuration')}
            className="w-10 bg-transparent text-right text-sm tabular-nums text-foreground outline-none"
          />
          <span className="text-xs text-muted-foreground">{t('booking.admin.types.field.durationUnitMin')}</span>
        </span>
      </div>

      {moreFields && (
        <div className="grid grid-cols-1 gap-x-4 gap-y-1 border-t border-border px-3.5 py-2 sm:grid-cols-2">
          <ToggleSwitch label={t('booking.admin.types.field.requiresApproval')} checked={requiresApproval} onChange={setRequiresApproval} />
          <ToggleSwitch label={t('booking.admin.types.field.allowInstant')} checked={allowInstant} onChange={setAllowInstant} />
          <ToggleSwitch label={t('booking.admin.types.field.enableWaitlist')} checked={enableWaitlist} onChange={setEnableWaitlist} />
          <ToggleSwitch label={t('booking.admin.types.field.enableReviews')} checked={enableReviews} onChange={setEnableReviews} />
        </div>
      )}
    </FormDialog>
  );
}
